'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as React from 'react';

import { cn } from '../../libs/utils';
import { semanticColor } from '../../theme/semanticColor';

type CheckboxSizeType = 'small' | 'medium';

interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  size?: CheckboxSizeType;
  isBox?: boolean;
}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root & { size?: number; isBox?: boolean }>, CheckboxProps>(
  ({ size = 'medium', className, ...props }, ref) => {
    const { checked, isBox, ...rest } = props;
    const getCheckboxSize = () => (size === 'small' ? 18 : 24);

    const checkboxSize = getCheckboxSize();

    const getCheckColor = () => {
      return checked ? semanticColor.primary.foreground.main : semanticColor.neutral.foreground.enabled;
    };

    const getCheckBoxType = () => {
      if (isBox && checked) {
        return (
          <svg width={checkboxSize} height={checkboxSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Checkbox">
              <path d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z" fill="#5E50FA" />
              <path
                id="checkmark"
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.6426 8.28967C18.0913 8.70095 18.1216 9.39806 17.7103 9.84672L11.5961 16.5168C11.1486 17.005 10.3843 17.0216 9.91605 16.5533L6.32278 12.96C5.89241 12.5296 5.89241 11.8319 6.32278 11.4015C6.75315 10.9711 7.45093 10.9711 7.8813 11.4015L10.7059 14.2261L16.0856 8.35737C16.4969 7.90871 17.194 7.8784 17.6426 8.28967Z"
                fill="white"
              />
            </g>
          </svg>
        );
      } else if (isBox && !checked) {
        return (
          <svg width={checkboxSize} height={checkboxSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Checkbox">
              <g clipPath="url(#clip0_2829_4669)">
                <path
                  id="checkmark"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M17.6426 8.28967C18.0913 8.70095 18.1216 9.39806 17.7103 9.84672L11.5961 16.5168C11.1486 17.005 10.3843 17.0216 9.91605 16.5533L6.32278 12.96C5.89241 12.5296 5.89241 11.8319 6.32278 11.4015C6.75315 10.9711 7.45093 10.9711 7.8813 11.4015L10.7059 14.2261L16.0856 8.35737C16.4969 7.90871 17.194 7.8784 17.6426 8.28967Z"
                  fill="#CBD1D7"
                />
                <path
                  id="outline"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12ZM12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0Z"
                  fill="#CBD1D7"
                />
              </g>
            </g>
            <defs>
              <clipPath id="clip0_2829_4669">
                <path d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z" fill="white" />
              </clipPath>
            </defs>
          </svg>
        );
      }
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width={checkboxSize} height={checkboxSize} viewBox="0 0 24 24" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.6426 8.28967C18.0913 8.70095 18.1216 9.39806 17.7103 9.84672L11.5961 16.5168C11.1486 17.005 10.3843 17.0216 9.91605 16.5533L6.32278 12.96C5.89241 12.5296 5.89241 11.8319 6.32278 11.4015C6.75315 10.9711 7.45093 10.9711 7.8813 11.4015L10.7059 14.2261L16.0856 8.35737C16.4969 7.90871 17.194 7.8784 17.6426 8.28967Z"
            fill={getCheckColor()}
          />
        </svg>
      );
    };

    return (
      <CheckboxPrimitive.Root ref={ref} className={cn(className, 'flex items-center justify-center')} {...rest}>
        {getCheckBoxType()}
      </CheckboxPrimitive.Root>
    );
  }
);

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
